import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import TopicCard from '@/components/feature/TopicCard';
import { useSEO } from '@/hooks/useSEO';
import { topics } from '@/mocks/topics';
import { categories } from '@/mocks/categories';
import FilterBar from './components/FilterBar';

const PAGE_SIZE = 6;

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') ?? 'all';
  const [query, setQuery] = useState(searchParams.get('q') ?? '');
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setQuery(searchParams.get('q') ?? '');
  }, [searchParams]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [activeCategory, sort, query]);

  const updateParam = (key: 'category' | 'q', value: string) => {
    const params = new URLSearchParams(searchParams);
    if (!value || value === 'all') {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    setSearchParams(params, { replace: true });
  };

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    let list = topics.filter((topic) => {
      const matchCategory = activeCategory === 'all' || topic.categorySlug === activeCategory;
      const matchKeyword =
        !keyword ||
        topic.title.toLowerCase().includes(keyword) ||
        topic.summary.toLowerCase().includes(keyword) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchKeyword;
    });
    list = list.sort((a, b) =>
      sort === 'hot' ? b.heat - a.heat : b.updatedAt.localeCompare(a.updatedAt)
    );
    return list;
  }, [activeCategory, query, sort]);

  const currentCategory = categories.find((item) => item.slug === activeCategory);
  const shown = filtered.slice(0, visible);

  const jsonLd = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: currentCategory ? `${currentCategory.name}话题列表` : '话题库 - 全部话题',
        description: currentCategory ? currentCategory.description : '黑料首页话题库，按栏目浏览全部娱乐热点与网络话题。',
        url: `${origin}/topics`,
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: origin || undefined },
          { '@type': 'ListItem', position: 2, name: '话题库' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        numberOfItems: filtered.length,
        itemListElement: filtered.slice(0, 10).map((topic, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: topic.title,
          url: `${origin}/topic/${topic.id}`,
        })),
      },
    ];
  }, [currentCategory, filtered]);

  useSEO({
    title: currentCategory
      ? `${currentCategory.name} - 话题库 | 黑料首页`
      : '话题库 - 全部话题与实时动态 | 黑料首页',
    description: currentCategory
      ? `${currentCategory.name}栏目共收录 ${currentCategory.count} 篇内容。${currentCategory.description}`
      : '黑料首页话题库，按明星动态、综艺热点、影视播报、网红观察、网络热议与行业风云分类浏览，支持关键词搜索与最新、最热排序。',
    keywords: currentCategory
      ? `${currentCategory.name},黑料首页,黑料首页最新,话题库`
      : '话题库,黑料首页,黑料首页最新,黑料首页动态,娱乐热点',
    canonicalPath: '/topics',
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '话题库', to: currentCategory ? '/topics' : undefined },
                ...(currentCategory ? [{ label: currentCategory.name }] : []),
              ]}
            />
            <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              {currentCategory ? currentCategory.name : '话题库'}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-700">
              {currentCategory
                ? currentCategory.description
                : '这里汇集了站内全部话题，按最新的更新时间倒序排列。你可以切换栏目、输入关键词，或按热度重新排序。'}
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
          <FilterBar
            activeCategory={activeCategory}
            onCategoryChange={(slug) => updateParam('category', slug)}
            sort={sort}
            onSortChange={setSort}
            query={query}
            onQueryChange={(value) => {
              setQuery(value);
              updateParam('q', value);
            }}
            total={filtered.length}
          />

          {shown.length > 0 ? (
            <>
              <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((topic) => (
                  <TopicCard key={topic.id} topic={topic} />
                ))}
              </div>

              {visible < filtered.length && (
                <div className="mt-10 text-center">
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-add-line" aria-hidden="true" />
                    加载更多话题（剩余 {filtered.length - visible} 条）
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                <i className="ri-search-eye-line" aria-hidden="true" />
              </span>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到匹配的话题</p>
              <p className="mt-2 text-sm text-foreground-600">试试更换关键词，或切换回「全部栏目」。</p>
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setSearchParams({}, { replace: true });
                }}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}